import { Context, InjectOptionsType, InjectModule } from '../types';

import { createDomElement } from './create-dom-element';
import { getModuleData } from './get-module-data';
import { makePostman } from './postman';
import { preloadHosts, PRELOAD } from './pre-loader';
import {
  context,
  getCustomContext,
  subscribeError,
  dispatchError,
} from './utils';

/**
 * @description Method for mount the module
 * @param callback on load callback
 */
async function renderModule(
  this: Context,
  callback?: (state?: boolean) => void,
): Promise<unknown> {
  const w = window as any;
  const { element, postman, name, host, props, manifestPath } = this;
  const key = `showcase_render_module_${name}`;
  const custom = getCustomContext();

  await PRELOAD.wait(host);

  const renderModule = (): void => {
    w['showcase_modules'][key].call({
      element,
      postman,
      context,
      host,
      props,
      custom,
    });
    if (callback) {
      callback(true);
    }
  };

  if (preloadHosts[host] && w[key]) {
    if (preloadHosts[host]?.css) {
      createDomElement('link', preloadHosts[host]?.css ?? '', renderModule).add(
        element.parentNode,
      );
    } else {
      renderModule();
    }

    return null;
  }

  // eslint-disable-next-line no-return-await
  return await getModuleData.call(
    this,
    () => {
      const renderFn = w['showcase_modules'][key];
      if (renderFn) {
        renderModule();
      } else {
        dispatchError(
          name,
          `The module named '${name}' does not exist or an internal error has occurred in it`,
        );
      }
    },
    manifestPath,
  );
}

/**
 * @description Method for unmounting the module
 * @param onLoad on load callback
 */
function unmountModule(this: Context, fn: () => void): void {
  const { element, name } = this;
  const w = window as any;
  const renderFunction =
    w['showcase_modules'][`showcase_render_module_${name}`];

  // unmountion only if module was mount
  if (renderFunction) {
    const unmountFunction =
      w['showcase_modules'][`showcase_unmount_module_${name}`];

    if (!unmountFunction) {
      dispatchError(
        name,
        `It is impossible to unsubscribe from the '${name}' module because it does not exist.`,
      );
      return;
    }

    // Call module unmount function
    if (element && name) {
      unmountFunction?.(element, context);
      fn();
    }
  }

  // Remove element after unmount
  // @ts-expect-error wrong types
  this.element = null;
}

/**
 * @description Before callback handler
 * @param callback include callback
 */
function beforeCallback(
  this: Context,
  callback: (file?: string) => void,
): void {
  this.beforeCallback = callback;
}

/**
 * @description Error Boundary
 * @param callback callback
 */
function moduleErrorBoundary(
  this: Context,
  callback: (error: string, errors: { [key: number]: string }) => void,
): void {
  subscribeError(this.name, callback);
}

/**
 * @description This method is used to connect third-party modules from the showcase
 * @return getModule, render, unmount, beforeCallback, getContext
 */
export function injectModule(options: InjectOptionsType): InjectModule {
  const { name } = options;
  const { getContext } = context;
  const postman = makePostman(name);
  const w = window as any;
  let virtual;

  // update status
  w.showcase_connected = 'connected';

  if (!w['showcase_modules']) {
    w['showcase_modules'] = {};
  }

  if (options.virtual) {
    virtual = {
      dom: false,
      window: true,
    };
  }

  // write this context
  const _this: Context = {
    beforeCallback: null,
    postman,
    virtual,
    ...options,
  };

  return {
    renderModule: renderModule.bind(_this),
    unmount: unmountModule.bind(_this),
    beforeCallback: beforeCallback.bind(_this),
    errorBoundary: moduleErrorBoundary.bind(_this),
    getContext,
  };
}
